import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FaSearch, FaTimes, FaFileAlt } from 'react-icons/fa';
import {
  adminSearch,
  clientSearch,
  searchAdminLedgerRows,
  searchClientLedgerRows,
} from '../../utils/api';
import './index.scss';

const GROUP_LABELS = {
  orders: 'Orders',
  clients: 'Clients',
  purchaseOrders: 'Purchase Orders',
  asns: 'ASNs',
  products: 'Products',
  shipments: 'Shipments',
  invoices: 'Invoices',
  warehouses: 'Warehouses',
  inventory: 'Inventory',
  ledgerRows: 'Ledger Rows',
};

function getResultNav(type, r, portal) {
  const adminBase = '';
  const clientBase = '/client';
  switch (type) {
    case 'orders':
      return `${portal === 'client' ? clientBase : adminBase}/orders/${r.orderId}`;
    case 'clients':
      return `/clients/${r.id}`;
    case 'asns':
      return `${portal === 'client' ? clientBase : adminBase}/asn/${r.id}`;
    case 'products':
      return portal === 'client' ? '/client/inventory' : `/product/${r.id}`;
    default:
      return null;
  }
}

function getResultPrimary(type, r) {
  switch (type) {
    case 'orders':
      return r.externalId || r.friendlyId || r.orderId;
    case 'clients':
      return r.name;
    case 'purchaseOrders':
      return r.displayId || r.friendlyId || r.poExternalId || `PO #${r.id}`;
    case 'asns':
      return r.skuSavvyFriendlyId || r.poNumber || `ASN #${r.id}`;
    case 'products':
      return r.name;
    case 'shipments':
      return r.trackingNumber || r.shipmentId;
    case 'invoices':
      return r.clientName || `Invoice #${r.id}`;
    case 'warehouses':
      return r.name;
    case 'inventory':
      return r.variantId;
    case 'ledgerRows':
      return r.displayId || r.referenceId || `Ledger row #${r.id}`;
    default:
      return JSON.stringify(r);
  }
}

function getResultSecondary(type, r) {
  switch (type) {
    case 'orders':
      return r.status || '';
    case 'clients':
      return r.contactEmail || r.identifierValue || '';
    case 'purchaseOrders':
      return [r.status, r.warehouseName].filter(Boolean).join(' · ');
    case 'asns':
      return r.status || '';
    case 'products':
      return r.productOwner || r.productType || '';
    case 'shipments':
      return [r.status, r.carrierService].filter(Boolean).join(' · ');
    case 'invoices':
      return r.status || '';
    case 'warehouses':
      return r.address?.city ? `${r.address.city}, ${r.address.state?.abbr ?? ''}` : '';
    case 'inventory':
      return r.onHandQty != null ? `${r.onHandQty} on hand` : '';
    case 'ledgerRows':
      return [r.eventType, r.description].filter(Boolean).join(' · ');
    default:
      return '';
  }
}

function ResultRow({ type, result, q, portal, onNavigate }) {
  const primary = getResultPrimary(type, result);
  const secondary = getResultSecondary(type, result);
  const directNav = getResultNav(type, result, portal);

  const handleClick = () => {
    if (directNav) {
      onNavigate(directNav);
    } else {
      const base = portal === 'client' ? '/client' : '';
      onNavigate(`${base}/search?q=${encodeURIComponent(q)}&type=${type}`);
    }
  };

  return (
    <button className="gs-result-row" onClick={handleClick}>
      <span className="gs-result-primary">{primary}</span>
      {secondary && <span className="gs-result-secondary">{secondary}</span>}
    </button>
  );
}

function SearchGroup({ group, q, portal, onNavigate }) {
  const { type, total, results } = group;
  if (!total || !results?.length) return null;
  const label = GROUP_LABELS[type] || type;
  const searchBase = portal === 'client' ? '/client' : '';

  return (
    <div className="gs-group">
      <div className="gs-group-header">
        <span className="gs-group-label">{label}</span>
        <span className="gs-group-count">{total}</span>
      </div>
      {results.map((r, i) => (
        <ResultRow key={i} type={type} result={r} q={q} portal={portal} onNavigate={onNavigate} />
      ))}
      {total > results.length && (
        <button
          className="gs-view-all"
          onClick={() =>
            onNavigate(`${searchBase}/search?q=${encodeURIComponent(q)}&type=${type}`)
          }>
          View all {total} →
        </button>
      )}
    </div>
  );
}

function DocsGroup({ group, q, portal, onNavigate, onSuggest }) {
  const { total, results, suggestion } = group;
  if (!total || !results?.length) return null;
  const searchBase = portal === 'client' ? '/client' : '';
  const helpBase = portal === 'client' ? '/client/help' : '/help';
  const shown = results.slice(0, 5);

  return (
    <div className="gs-group gs-docs-group">
      <div className="gs-group-header">
        <span className="gs-group-label">Help &amp; Docs</span>
        <span className="gs-group-count">{total}</span>
      </div>
      {suggestion && (
        <div className="gs-docs-suggestion">
          Did you mean{' '}
          <button className="gs-docs-suggestion__link" onClick={() => onSuggest(suggestion)}>
            <strong>{suggestion}</strong>
          </button>
          ?
        </div>
      )}
      {shown.map((r, i) => (
        <button
          key={i}
          className="gs-result-row gs-doc-row"
          onClick={() => onNavigate(`${helpBase}/${r.slug}`)}>
          <FaFileAlt className="gs-doc-icon" size={12} />
          <span className="gs-doc-content">
            <span className="gs-result-primary">{r.title}</span>
            {r.snippet && <span className="gs-doc-snippet">{r.snippet}</span>}
          </span>
        </button>
      ))}
      {total > 5 && (
        <button
          className="gs-view-all"
          onClick={() => onNavigate(`${searchBase}/search?q=${encodeURIComponent(q)}&type=docs`)}>
          See all {total} docs results →
        </button>
      )}
    </div>
  );
}

function GlobalSearch({ portal = 'admin', storeId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQ('');
    setGroups([]);
    setLoading(false);
    clearTimeout(debounceRef.current);
  }, []);

  const openSearch = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const doSearch = useCallback(
    (query) => {
      clearTimeout(debounceRef.current);
      if (query.length < 2) {
        setGroups([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        const [res, ledgerRes] = await Promise.all([
          portal === 'admin' ? adminSearch(query, storeId) : clientSearch(query),
          portal === 'admin'
            ? storeId
              ? searchAdminLedgerRows(query, storeId, 5, 0)
              : Promise.resolve(null)
            : searchClientLedgerRows(query, 5, 0),
        ]);
        setLoading(false);
        if (res?.success) {
          const nextGroups = [...(res.groups ?? [])];
          if (ledgerRes?.success !== false && ledgerRes?.total > 0) {
            nextGroups.push({
              type: 'ledgerRows',
              total: ledgerRes.total,
              results: ledgerRes.results ?? [],
            });
          }
          setGroups(nextGroups);
        } else setGroups([]);
      }, 300);
    },
    [portal, storeId],
  );

  const handleInput = (e) => {
    const val = e.target.value;
    setQ(val);
    doSearch(val);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (e.key === 'Enter' && q.trim().length >= 2) {
      const base = portal === 'client' ? '/client' : '';
      navigate(`${base}/search?q=${encodeURIComponent(q.trim())}`);
      closeSearch();
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) closeSearch();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeSearch]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const dataGroups = groups.filter((g) => g.total > 0 && g.type !== 'docs');
  const docsGroup = groups.find((g) => g.type === 'docs' && g.total > 0);
  const nonEmptyGroups = groups.filter((g) => g.total > 0);
  const showEmpty = !loading && q.length >= 2 && groups.length > 0 && nonEmptyGroups.length === 0;

  const handleNav = (path) => {
    navigate(path);
    closeSearch();
  };

  return (
    <>
      <button className="gs-icon-btn" onClick={openSearch} title="Search">
        <FaSearch size={18} />
      </button>

      {open && (
        <div className="gs-overlay" role="dialog" aria-modal="true">
          <div className="gs-panel" ref={panelRef}>
            <div className="gs-input-wrap">
              <FaSearch className="gs-input-icon" size={14} />
              <input
                ref={inputRef}
                type="text"
                className="gs-input"
                value={q}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder="Search orders, clients, products, ASNs… (press Enter for all results)"
                autoComplete="off"
                spellCheck="false"
              />
              {loading && <Spinner animation="border" size="sm" className="gs-spinner" />}
              <button className="gs-close-btn" onClick={closeSearch} title="Close">
                <FaTimes size={14} />
              </button>
            </div>

            {(nonEmptyGroups.length > 0 || showEmpty) && (
              <div className="gs-dropdown">
                {dataGroups.map((g) => (
                  <SearchGroup
                    key={g.type}
                    group={g}
                    q={q}
                    portal={portal}
                    onNavigate={handleNav}
                  />
                ))}
                {docsGroup && (
                  <>
                    {dataGroups.length > 0 && <div className="gs-docs-divider" />}
                    <DocsGroup
                      group={docsGroup}
                      q={q}
                      portal={portal}
                      onNavigate={handleNav}
                      onSuggest={(suggestion) => {
                        setQ(suggestion);
                        doSearch(suggestion);
                      }}
                    />
                  </>
                )}
                {showEmpty && (
                  <div className="gs-empty">
                    No results for <strong>"{q}"</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default GlobalSearch;
